import React, { useMemo, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Box, Chip, Paper, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import BackHandOutlinedIcon from '@mui/icons-material/BackHandOutlined';
import { brandColors, getContestGenderAccent, getGradeColor, getGradeTierLabel } from '@config/brandTokens';
import { ContestAgeBadge } from '@components/contest/ContestAgeBadge';
import CapabilityLevelChip from '@components/contest/CapabilityLevelChip';
import { getCategoryRowStyle } from '@components/contest/contestCategoryTheme';
import type { ContestCatalogEntry, ContestCategoryId, ContestGenderKey } from '../../types/contestCatalog';
import type { ContestCategoryGroup } from '@utils/contestCatalog';
import {
  getBenchmarkUnit,
  getContestGenderKeys,
  getContestGenderLabel,
  isAdultContestAge,
  getGradeBandsForAge,
  getTargetTimeDisplay,
  resolveScorecardGradeCodes,
} from '@utils/contestCatalog';
import {
  getBenchmarkRecordRows,
  type BenchmarkRecordKind,
} from '@/content/benchmarkRecordsPlaceholder';

const FONT = '"Sora", Arial, sans-serif';
const BODY_FONT = '0.875rem';
const HEADER_FONT = '0.875rem';

const tableSx = {
  width: '100%',
  borderCollapse: 'collapse' as const,
  tableLayout: 'fixed' as const,
  fontFamily: FONT,
  fontSize: BODY_FONT,
  '& th': {
    fontSize: HEADER_FONT,
    fontWeight: 700,
    color: brandColors.navy,
    bgcolor: '#f1f5f9',
    borderBottom: '2px solid #cbd5e1',
    px: 1.5,
    py: 1,
    textAlign: 'center' as const,
    verticalAlign: 'middle' as const,
    lineHeight: 1.3,
  },
  '& td': {
    fontSize: BODY_FONT,
    color: brandColors.slate,
    borderBottom: '1px solid #e2e8f0',
    px: 1.5,
    py: 1,
    verticalAlign: 'middle' as const,
    lineHeight: 1.4,
  },
};

function categoryHeaderRow(colSpan: number, categoryId: ContestCategoryId, withTopBorder = false) {
  const theme = getCategoryRowStyle(categoryId);
  return (
    <tr key={`cat-${categoryId}`}>
      <Box
        component="td"
        colSpan={colSpan}
        sx={{
          px: 1.5,
          py: 0.75,
          background: theme.headerBg,
          borderBottom: `2px solid ${theme.accent}`,
          borderTop: withTopBorder ? '2px solid #e2e8f0' : undefined,
        }}
      >
        <Typography
          sx={{
            fontWeight: 900,
            color: theme.headerText,
            letterSpacing: 1.4,
            textTransform: 'uppercase',
            fontSize: '0.82rem',
            fontFamily: FONT,
          }}
        >
          {theme.label}
        </Typography>
      </Box>
    </tr>
  );
}

const genderRowTint = (genderKey: ContestGenderKey) => `${getContestGenderAccent(genderKey).accent}14`;

export interface ContestSkillBenchmarkBlockProps {
  contest: ContestCatalogEntry | null;
  ageCategory: string;
}

/** Compact benchmark summary for Learn Frame 2 (selected skill) — dual Boy/Girl lines. */
export const ContestSkillBenchmarkBlock: React.FC<ContestSkillBenchmarkBlockProps> = ({
  contest,
  ageCategory,
}) => {
  if (!contest) {
    return (
      <Typography variant="body2" sx={{ color: brandColors.slate, fontFamily: FONT }}>
        Select a skill to view benchmarks.
      </Typography>
    );
  }

  const headerCodes = resolveScorecardGradeCodes([contest], ageCategory);
  const unit = getBenchmarkUnit(contest, ageCategory);

  return (
    <Box sx={{ fontFamily: FONT, fontSize: BODY_FONT }}>
      {unit ? (
        <Typography sx={{ fontWeight: 600, color: brandColors.slate, mb: 1 }}>
          Unit: <Box component="span" sx={{ fontWeight: 700, color: brandColors.navy }}>{unit}</Box>
        </Typography>
      ) : null}
      <Box
        component="table"
        sx={{
          ...tableSx,
          tableLayout: 'fixed',
          '& th, & td': { px: 0.35, py: 0.4, fontSize: '0.7rem' },
        }}
      >
        <colgroup>
          <col style={{ width: isAdultContestAge(ageCategory) ? 62 : 40 }} />
          <col style={{ width: 42 }} />
          {headerCodes.map((code) => (
            <col key={code} />
          ))}
        </colgroup>
        <thead>
          <tr>
            <Box component="th" sx={{ textAlign: 'left !important', fontSize: '0.62rem', px: 0.3, lineHeight: 1.15 }}>
              Gender
            </Box>
            <Box component="th" sx={{ fontSize: '0.62rem', px: 0.2, lineHeight: 1.15 }} title="Target time">
              Target
            </Box>
            {headerCodes.map((code) => {
              const c = getGradeColor(code);
              const tier = getGradeTierLabel(code);
              return (
                <Box
                  component="th"
                  key={code}
                  title={`${code} · ${tier}`}
                  sx={{
                    bgcolor: `${c.bg} !important`,
                    color: `${c.text} !important`,
                    fontWeight: 700,
                    px: 0.3,
                    verticalAlign: 'bottom',
                  }}
                >
                  {code}
                  <Box
                    component="span"
                    sx={{
                      display: 'block',
                      fontWeight: 600,
                      fontSize: '0.58rem',
                      mt: 0.15,
                      lineHeight: 1.15,
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      hyphens: 'auto',
                    }}
                  >
                    {tier}
                  </Box>
                </Box>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {getContestGenderKeys(ageCategory).map((genderKey) => {
            const grades = getGradeBandsForAge(contest, ageCategory, genderKey, {
              fallbackOtherGender: false,
            });
            const gradeByCode = Object.fromEntries(grades.map((g) => [String(g.code), g]));
            const targetTime = getTargetTimeDisplay(contest, ageCategory, genderKey);
            return (
              <Box component="tr" key={genderKey} sx={{ bgcolor: genderRowTint(genderKey) }}>
                <Box
                  component="td"
                  sx={{
                    textAlign: 'left',
                    fontWeight: 800,
                    fontSize: '0.65rem',
                    color: getContestGenderAccent(genderKey).accent,
                    lineHeight: 1.15,
                    px: 0.3,
                  }}
                >
                  {getContestGenderLabel(ageCategory, genderKey)}
                </Box>
                <Box
                  component="td"
                  sx={{
                    textAlign: 'center',
                    fontWeight: 600,
                    fontSize: '0.65rem',
                    color: brandColors.navy,
                    whiteSpace: 'nowrap',
                    px: 0.2,
                  }}
                  title={targetTime.title}
                >
                  {targetTime.label}
                </Box>
                {headerCodes.map((code) => {
                  const band = gradeByCode[code];
                  const c = getGradeColor(code);
                  return (
                    <Box
                      component="td"
                      key={code}
                      title={band?.label ? String(band.label) : undefined}
                      sx={{
                        textAlign: 'center',
                        fontWeight: 600,
                        color: band?.label ? c.text : '#94a3b8',
                        px: 0.3,
                      }}
                    >
                      {band?.label ?? '—'}
                    </Box>
                  );
                })}
              </Box>
            );
          })}
        </tbody>
      </Box>
    </Box>
  );
};

export interface ContestBenchmarkTabPanelProps {
  categoryGroups: ContestCategoryGroup[];
  ageCategory: string;
  sportName: string;
  selectedContestId?: string | null;
  onSelectContest?: (contestId: string) => void;
}

function flattenContests(groups: ContestCategoryGroup[]) {
  return groups.flatMap((g) => g.contests);
}

const BENCHMARK_GUIDELINE_SECTIONS: { heading: string; points: { title: string; body: string }[] }[] = [
  {
    heading: 'Purpose and Objective',
    points: [
      {
        title: 'Developmental & Performance',
        body: 'The primary objective of these benchmarks is to evaluate and establish a solid technical foundation before athletes enter competitive tournaments.',
      },
      {
        title: 'Targeted Preparation',
        body: 'These benchmarks serve as a tool to streamline and improve tournament preparation, rather than serving as elite performance metrics.',
      },
    ],
  },
  {
    heading: 'Understanding the Benchmarks',
    points: [
      {
        title: 'Baseline Performance',
        body: 'These benchmarks measure average performance levels that can be comfortably demonstrated within a specific timeframe to test and validate fundamental skills. They are not intended for establishing or breaking records.',
      },
      {
        title: 'Not for High-Intensity Training',
        body: 'These guidelines are strictly not designed for high-performance or high-intensity training. High-intensity training aimed solely at meeting these benchmarks is strongly discouraged for young athletes aged 5–15.',
      },
    ],
  },
  {
    heading: 'Focus and Execution',
    points: [
      {
        title: 'Consistency Over Intensity',
        body: 'The training focus must remain entirely on mastering technical foundation skills, building consistency, and ensuring long-term athletic development.',
      },
      {
        title: 'Tracking Progress',
        body: 'Benchmark assessments record both passed and failed attempts to accurately track development. It is highly advised not to push athletes to regularly exceed these age-specific baseline numbers.',
      },
    ],
  },
  {
    heading: 'Revisions and Authorized Use',
    points: [
      {
        title: 'Dynamic Benchmarks',
        body: 'These numbers are subject to revision based on actual ongoing player performance and their dependency on other sport-specific skills.',
      },
      {
        title: 'Usage Restrictions',
        body: 'These metrics are designed strictly for their intended evaluation purposes. Using these numbers for any other purpose requires explicit, prior approval from EncycGames.',
      },
    ],
  },
];

const BenchmarkGuidelinesSection: React.FC = () => {
  const [expanded, setExpanded] = useState(false);

  return (
    <Accordion
      disableGutters
      elevation={0}
      expanded={expanded}
      onChange={(_, next) => setExpanded(next)}
      sx={{
        borderBottom: '1px solid #e2e8f0',
        bgcolor: brandColors.ice,
        mb: 1.5,
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: expanded ? '#0D9488' : brandColors.slate }} />}
        sx={{
          px: { xs: 1.5, md: 2 },
          py: 0.25,
          minHeight: 48,
          bgcolor: expanded ? '#ecfdf5' : 'rgba(251, 191, 36, 0.12)',
          borderLeft: '4px solid #f59e0b',
          transition: 'background-color 0.2s ease',
          '&:hover': { bgcolor: expanded ? '#d1fae5' : 'rgba(251, 191, 36, 0.18)' },
          '& .MuiAccordionSummary-content': { my: 0.75, alignItems: 'center', gap: 1 },
        }}
      >
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.35,
            flexShrink: 0,
            color: '#d97706',
          }}
          aria-hidden
        >
          <BackHandOutlinedIcon sx={{ fontSize: 22, transform: 'rotate(-20deg)' }} />
          <LightbulbOutlinedIcon sx={{ fontSize: 22, color: '#ca8a04' }} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              mb: 0.2,
            }}
          >
            <Typography
              component="span"
              sx={{
                fontWeight: 800,
                fontSize: '0.65rem',
                letterSpacing: 0.7,
                textTransform: 'uppercase',
                color: '#b45309',
                fontFamily: FONT,
                bgcolor: 'rgba(245, 158, 11, 0.2)',
                px: 0.7,
                py: 0.15,
                borderRadius: 0.75,
                lineHeight: 1.4,
              }}
            >
              Must read
            </Typography>
          </Box>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: { xs: '0.82rem', md: '0.88rem' },
              color: brandColors.navy,
              fontFamily: FONT,
              lineHeight: 1.35,
              pr: 1,
            }}
          >
            Skills & Fitness Benchmarks: Guidelines and Objectives
          </Typography>
          <Typography
            sx={{
              mt: 0.35,
              fontWeight: 500,
              fontSize: '0.72rem',
              color: brandColors.slate,
              fontFamily: FONT,
              lineHeight: 1.35,
              pr: 1,
            }}
          >
            Applies to every age category (U9–Senior / Para) — read once, then use the age-specific average benchmarks below.
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          px: { xs: 1.5, md: 2 },
          pt: 0,
          pb: 1.75,
          bgcolor: brandColors.white,
          borderTop: '1px solid #e2e8f0',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1.25 }}>
          {BENCHMARK_GUIDELINE_SECTIONS.map((section) => (
            <Box key={section.heading}>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: '0.78rem',
                  letterSpacing: 0.4,
                  textTransform: 'uppercase',
                  color: '#0D9488',
                  fontFamily: FONT,
                  mb: 0.6,
                }}
              >
                {section.heading}
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.25, display: 'flex', flexDirection: 'column', gap: 0.65 }}>
                {section.points.map((point) => (
                  <Box component="li" key={point.title} sx={{ color: brandColors.slate }}>
                    <Typography
                      component="span"
                      sx={{ fontWeight: 700, color: brandColors.navy, fontSize: '0.82rem', fontFamily: FONT }}
                    >
                      {point.title}:{' '}
                    </Typography>
                    <Typography
                      component="span"
                      sx={{ fontWeight: 500, fontSize: '0.82rem', lineHeight: 1.45, fontFamily: FONT }}
                    >
                      {point.body}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

const recordTypeSx = (kind: BenchmarkRecordKind) => {
  if (kind === 'Speed') return { bgcolor: '#e0f2fe', color: '#0369a1', borderColor: '#7dd3fc' };
  if (kind === 'Power') return { bgcolor: '#fff7ed', color: brandColors.orange, borderColor: '#fdba74' };
  if (kind === 'Aggression') return { bgcolor: '#fef2f2', color: '#b91c1c', borderColor: '#fecaca' };
  return { bgcolor: '#f1f5f9', color: brandColors.navy, borderColor: '#cbd5e1' };
};

const BenchmarkRecordsSection: React.FC<{ sportName: string; ageCategory: string }> = ({
  sportName,
  ageCategory,
}) => {
  const rows = getBenchmarkRecordRows(sportName);

  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        borderRadius: 2.5,
        border: '1px solid #e2e8f0',
        overflow: 'hidden',
        bgcolor: brandColors.white,
        boxShadow: '0 1px 4px rgba(17,23,57,0.06)',
      }}
    >
      <Box
        sx={{
          px: { xs: 1.5, md: 2 },
          py: 1.25,
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          gap: 1,
          flexWrap: 'wrap',
          borderBottom: `2px solid ${brandColors.orange}`,
          bgcolor: brandColors.white,
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: { xs: '0.95rem', md: '1.05rem' },
              color: brandColors.navy,
              fontFamily: FONT,
              lineHeight: 1.3,
            }}
          >
            Benchmark Records — {sportName} top performances
          </Typography>
          <Typography
            sx={{
              mt: 0.45,
              fontSize: '0.82rem',
              fontWeight: 500,
              color: brandColors.slate,
              fontFamily: FONT,
              lineHeight: 1.45,
              maxWidth: 760,
            }}
          >
            Record marks for high-intensity, skilled work — speed, power, aggression, and hard
            execution. Distinct from the age-band <strong>averages</strong> above. Live records
            data ships later; values here are sample placeholders.
          </Typography>
        </Box>
        <Chip
          size="small"
          label="Coming soon"
          sx={{
            fontWeight: 800,
            fontFamily: FONT,
            bgcolor: '#fff7ed',
            color: brandColors.orange,
            border: `1px solid ${brandColors.orange}55`,
          }}
        />
      </Box>

      <Typography
        sx={{
          px: { xs: 1.5, md: 2 },
          py: 1,
          fontSize: BODY_FONT,
          fontWeight: 400,
          color: brandColors.slate,
          fontFamily: FONT,
          bgcolor: brandColors.ice,
          borderBottom: '1px solid #e2e8f0',
        }}
      >
        Sample {sportName} records across age bands (viewing {ageCategory}). Not a personal
        leaderboard and not the average benchmark table.
      </Typography>

      <Box sx={{ width: '100%', overflowX: 'auto' }}>
        <Box component="table" sx={{ ...tableSx, minWidth: 640 }}>
          <thead>
            <tr>
              <Box component="th" sx={{ textAlign: 'left !important' }}>
                Skill
              </Box>
              <Box component="th">Record type</Box>
              <Box component="th">Age band</Box>
              <Box component="th">Value</Box>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const typeStyle = recordTypeSx(row.recordType);
              return (
                <Box component="tr" key={row.id}>
                  <Box
                    component="td"
                    sx={{ textAlign: 'left', fontWeight: 700, color: brandColors.navy }}
                  >
                    {row.skill}
                  </Box>
                  <Box component="td" sx={{ textAlign: 'center' }}>
                    <Chip
                      size="small"
                      label={row.recordType}
                      sx={{
                        height: 22,
                        fontWeight: 800,
                        fontSize: '0.68rem',
                        fontFamily: FONT,
                        border: '1px solid',
                        ...typeStyle,
                      }}
                    />
                  </Box>
                  <Box
                    component="td"
                    sx={{ textAlign: 'center', fontWeight: 700, color: brandColors.navy }}
                  >
                    {row.ageBand}
                  </Box>
                  <Box component="td" sx={{ textAlign: 'center', fontWeight: 600 }}>
                    {row.value}{' '}
                    <Box component="span" sx={{ color: brandColors.slate, fontWeight: 500 }}>
                      {row.unit}
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </tbody>
        </Box>
      </Box>
    </Paper>
  );
};

/** Full-width benchmark reference table — one row per skill; Boy/Girl values stack only when they differ. */
export const ContestBenchmarkTabPanel: React.FC<ContestBenchmarkTabPanelProps> = ({
  categoryGroups,
  ageCategory,
  sportName,
  selectedContestId = null,
  onSelectContest,
}) => {
  const allContests = useMemo(() => flattenContests(categoryGroups), [categoryGroups]);
  const gradeCodes = useMemo(
    () => resolveScorecardGradeCodes(allContests, ageCategory),
    [allContests, ageCategory],
  );
  const skillCount = allContests.length;
  const averageBenchmarkTitle = `${ageCategory} - ${sportName} Developmental & Performance Capabilities - Average Benchmark`;
  const colSpan = 5 + gradeCodes.length;
  const genderKeys = getContestGenderKeys(ageCategory);

  if (!skillCount) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%' }}>
        <Typography variant="body2" sx={{ color: brandColors.slate, p: 3, textAlign: 'center', fontFamily: FONT }}>
          No skills published for this contest yet.
        </Typography>
        <BenchmarkRecordsSection sportName={sportName} ageCategory={ageCategory} />
      </Box>
    );
  }

  let rowNumber = 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%' }}>
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        borderRadius: 2.5,
        border: '1px solid #e2e8f0',
        overflow: 'hidden',
        bgcolor: brandColors.white,
        boxShadow: '0 1px 4px rgba(17,23,57,0.06)',
      }}
    >
      <Box sx={{ px: { xs: 0, md: 0 }, pb: 0.5 }}>
        <BenchmarkGuidelinesSection />
      </Box>

      <Box
        sx={{
          px: { xs: 1.5, md: 2 },
          py: 1.25,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          borderTop: '1px solid #e2e8f0',
          borderBottom: '2px solid #0D9488',
          bgcolor: brandColors.white,
        }}
      >
        <Typography
          sx={{
            flex: 1,
            fontWeight: 800,
            fontSize: { xs: '0.95rem', md: '1.05rem' },
            color: brandColors.navy,
            fontFamily: FONT,
            lineHeight: 1.3,
          }}
        >
          {averageBenchmarkTitle}
        </Typography>
        <ContestAgeBadge age={ageCategory} />
      </Box>

      <Typography
        sx={{
          px: { xs: 1.5, md: 2 },
          py: 1,
          fontSize: BODY_FONT,
          fontWeight: 400,
          color: brandColors.slate,
          fontFamily: FONT,
          bgcolor: brandColors.ice,
          borderBottom: '1px solid #e2e8f0',
        }}
      >
        {skillCount} skill{skillCount === 1 ? '' : 's'} for age {ageCategory} —{' '}
        {isAdultContestAge(ageCategory)
          ? 'Male, Female, Open and Gender diverse'
          : 'Boy and Girl'}{' '}
        shown on separate rows per skill. Click a skill to compare ages.
      </Typography>

      <Box sx={{ width: '100%', overflowX: 'auto', pt: 0.5 }}>
        <Box component="table" sx={{ ...tableSx, minWidth: 680, tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: 36 }} />
            <col style={{ width: '20%' }} />
            <col style={{ width: 96 }} />
            <col style={{ width: isAdultContestAge(ageCategory) ? 110 : 56 }} />
            <col style={{ width: 64 }} />
            {gradeCodes.map((code) => (
              <col key={code} />
            ))}
          </colgroup>
          <thead>
            <tr>
              <Box component="th">#</Box>
              <Box component="th" sx={{ textAlign: 'left !important' }}>
                Contest
              </Box>
              <Box component="th" sx={{ fontSize: '0.72rem', px: 0.4, lineHeight: 1.15 }} title="Capability Level">
                Capability
                <Box component="span" sx={{ display: 'block', fontWeight: 600, fontSize: '0.68rem' }}>
                  Level
                </Box>
              </Box>
              <Box component="th" sx={{ fontSize: '0.78rem', px: 0.5 }}>
                Gender
              </Box>
              <Box component="th" sx={{ fontSize: '0.78rem', px: 0.5 }} title="Target time">
                Target
              </Box>
              {gradeCodes.map((code) => {
                const c = getGradeColor(code);
                const tier = getGradeTierLabel(code);
                return (
                  <Box
                    component="th"
                    key={code}
                    title={`${code} · ${tier}`}
                    sx={{
                      bgcolor: `${c.bg} !important`,
                      color: `${c.text} !important`,
                      borderBottom: `2px solid ${c.border} !important`,
                      px: 0.75,
                    }}
                  >
                    {code}
                    <Box
                      component="span"
                      sx={{
                        display: 'block',
                        fontWeight: 600,
                        fontSize: '0.72rem',
                        mt: 0.15,
                        lineHeight: 1.2,
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                      }}
                    >
                      {tier}
                    </Box>
                  </Box>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {categoryGroups.map((group, groupIndex) => {
              if (!group.contests.length) return null;
              const theme = getCategoryRowStyle(group.id);
              return (
                <React.Fragment key={group.id}>
                  {categoryHeaderRow(colSpan, group.id, groupIndex > 0)}
                  {group.contests.map((contest) => {
                    rowNumber += 1;
                    const selected = contest.id === selectedContestId;
                    return (
                      <React.Fragment key={contest.id}>
                        {genderKeys.map((genderKey, genderIndex) => {
                          const grades = getGradeBandsForAge(contest, ageCategory, genderKey, {
                            fallbackOtherGender: false,
                          });
                          const gradeByCode = Object.fromEntries(grades.map((g) => [String(g.code), g]));
                          const targetTime = getTargetTimeDisplay(contest, ageCategory, genderKey);
                          const isFirstGender = genderIndex === 0;
                          return (
                            <Box
                              component="tr"
                              key={`${contest.id}-${genderKey}`}
                              onClick={onSelectContest ? () => onSelectContest(contest.id) : undefined}
                              sx={{
                                cursor: onSelectContest ? 'pointer' : 'default',
                                bgcolor: selected
                                  ? `${theme.accent}14`
                                  : genderRowTint(genderKey),
                                boxShadow:
                                  selected && isFirstGender
                                    ? `inset 8px 0 0 ${theme.accent}`
                                    : selected
                                      ? `inset 8px 0 0 ${theme.accent}88`
                                      : 'none',
                                outline: selected ? `1px solid ${theme.accent}55` : 'none',
                                transition: 'background-color 0.15s ease, box-shadow 0.15s ease',
                                '& td': {
                                  borderBottom:
                                    genderIndex === genderKeys.length - 1
                                      ? '1px solid #e2e8f0'
                                      : '1px solid #f1f5f9',
                                },
                                '&:hover': onSelectContest
                                  ? {
                                      bgcolor: selected
                                        ? `${theme.accent}20`
                                        : `${getContestGenderAccent(genderKey).accent}24`,
                                    }
                                  : undefined,
                              }}
                            >
                              <Box
                                component="td"
                                sx={{
                                  textAlign: 'center',
                                  fontWeight: 700,
                                  color: theme.rowTarget,
                                  bgcolor: 'inherit',
                                  verticalAlign: 'middle',
                                }}
                              >
                                {isFirstGender ? rowNumber : ''}
                              </Box>
                              <Box
                                component="td"
                                sx={{
                                  textAlign: 'left',
                                  fontWeight: selected ? 800 : 600,
                                  color: theme.rowText,
                                  bgcolor: 'inherit',
                                  verticalAlign: 'middle',
                                }}
                              >
                                {isFirstGender ? contest.title : ''}
                              </Box>
                              <Box
                                component="td"
                                sx={{
                                  textAlign: 'center',
                                  bgcolor: 'inherit',
                                  verticalAlign: 'middle',
                                  px: 0.4,
                                }}
                              >
                                {isFirstGender ? <CapabilityLevelChip level={contest.capabilityLevel} compact /> : ''}
                              </Box>
                              <Box
                                component="td"
                                sx={{
                                  textAlign: 'center',
                                  fontWeight: 800,
                                  fontSize: '0.78rem',
                                  color: getContestGenderAccent(genderKey).accent,
                                  bgcolor: 'inherit',
                                  whiteSpace: 'nowrap',
                                  px: 0.5,
                                  minWidth: 88,
                                }}
                              >
                                {getContestGenderLabel(ageCategory, genderKey)}
                              </Box>
                              <Box
                                component="td"
                                sx={{
                                  textAlign: 'center',
                                  fontWeight: 600,
                                  color: theme.rowText,
                                  bgcolor: 'inherit',
                                  whiteSpace: 'nowrap',
                                  px: 0.5,
                                }}
                                title={targetTime.title}
                              >
                                {targetTime.label}
                              </Box>
                              {gradeCodes.map((code) => {
                                const band = gradeByCode[code];
                                const c = getGradeColor(code);
                                const label = band?.label ? String(band.label) : '—';
                                return (
                                  <Box
                                    component="td"
                                    key={code}
                                    sx={{
                                      textAlign: 'center',
                                      fontWeight: 600,
                                      color: label !== '—' ? c.text : '#94a3b8',
                                      bgcolor: 'inherit',
                                    }}
                                  >
                                    {label}
                                  </Box>
                                );
                              })}
                            </Box>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
        </Box>
      </Box>
    </Paper>
      <BenchmarkRecordsSection sportName={sportName} ageCategory={ageCategory} />
    </Box>
  );
};
